import { useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { MapPinIcon } from '@heroicons/react/24/outline'
import { workoutPlannerAPI } from '../../services/api'

/**
 * Where else this exercise can be performed — VH-143.
 *
 * The Equipment dropdown above picks ONE machine. Since VH-139 the system also
 * links that machine's twins at other gyms automatically (same name, same
 * type), which is what lets one exercise be possible at several places. That
 * automation was invisible and unchangeable, and it cannot infer the case Reto
 * actually has: the Squat Rack at Avaloq Manegg also serves as a dip station,
 * so it shares no name with Lugano's Dip Station and would never be linked.
 *
 * So this lists every machine, grouped by gym, with the linked ones ticked.
 * Automation proposes; the user disposes.
 *
 * Saves on toggle rather than on the form's Save. The links are a separate
 * relation from the exercise row, and deferring them to submit would mean a
 * half-saved edit if one call succeeded and the other failed.
 *
 * The primary machine is shown ticked and DISABLED: it is what the exercise is
 * defined against, and an exercise available nowhere would silently disappear
 * from every location view. Change it in the dropdown instead.
 */
export default function ExerciseAvailabilityPicker({ exerciseId }) {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const isGerman = (i18n.language || 'en').startsWith('de')

  const { data, isLoading, isError } = useQuery({
    queryKey: ['exerciseAvailability', exerciseId],
    queryFn: async () => (await workoutPlannerAPI.getExerciseAvailability(exerciseId)).data,
    enabled: Boolean(exerciseId),
  })

  const mutation = useMutation({
    mutationFn: (ids) => workoutPlannerAPI.setExerciseAvailability(exerciseId, ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exerciseAvailability', exerciseId] })
      // The location filters and coverage counts are derived from these links.
      queryClient.invalidateQueries({ queryKey: ['exercises'] })
      queryClient.invalidateQueries({ queryKey: ['trainingLocations'] })
    },
  })

  // Grouped by gym, because "which machine" is only meaningful with "where".
  const byLocation = useMemo(() => {
    const groups = new Map()
    for (const eq of data?.equipment || []) {
      const key = eq.location_name || t('exercises.availability.noLocation')
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key).push(eq)
    }
    return [...groups.entries()]
  }, [data, t])

  if (!exerciseId) return null

  const linkedIds = (data?.equipment || []).filter((e) => e.is_linked).map((e) => e.id)

  const toggle = (eq) => {
    if (eq.is_primary || mutation.isPending) return
    const next = eq.is_linked
      ? linkedIds.filter((id) => id !== eq.id)
      : [...linkedIds, eq.id]
    mutation.mutate(next)
  }

  return (
    <div data-testid="exercise-availability">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {t('exercises.availability.title')}
      </label>
      <p className="text-xs text-gray-500 mb-2">{t('exercises.availability.help')}</p>

      {isLoading ? (
        <p className="text-xs text-gray-500">{t('exercises.availability.loading')}</p>
      ) : isError ? (
        <p className="text-xs text-red-600">{t('exercises.availability.loadFailed')}</p>
      ) : (
        <div className="border border-outline bg-surface-container max-h-56 overflow-y-auto">
          {byLocation.map(([locationName, items]) => (
            <div key={locationName} className="px-3 py-2 border-b border-outline last:border-b-0">
              <div className="flex items-center text-xs font-semibold text-gray-700 mb-1">
                <MapPinIcon className="h-3 w-3 mr-1" />
                {locationName}
              </div>
              {items.map((eq) => (
                <label
                  key={eq.id}
                  className={`flex items-center text-xs py-0.5 ${
                    eq.is_primary ? 'text-gray-500' : 'text-gray-700 cursor-pointer'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={Boolean(eq.is_linked)}
                    disabled={eq.is_primary || mutation.isPending}
                    onChange={() => toggle(eq)}
                    data-testid={`availability-${eq.id}`}
                    className="mr-2"
                  />
                  <span>{(isGerman ? eq.name_de : eq.name_en) || eq.name_en || eq.name_de}</span>
                  {eq.is_primary && (
                    <span className="ml-2 text-[10px] uppercase tracking-wide text-gray-400">
                      {t('exercises.availability.primary')}
                    </span>
                  )}
                </label>
              ))}
            </div>
          ))}
        </div>
      )}

      {mutation.isError && (
        <p className="text-xs text-red-600 mt-1">{t('exercises.availability.saveFailed')}</p>
      )}
    </div>
  )
}
